<template>
  <DialogRoot :open="open" @update:open="handleOpenChange">
    <DialogPortal>
      <Transition name="profile-video-overlay">
        <DialogOverlay v-if="open" class="profile-video-overlay" @click="close" />
      </Transition>
      <Transition name="profile-video-content">
        <DialogContent v-if="open" class="profile-video-dialog" @escape-key-down="close">
          <div class="profile-video-shell">
            <DialogTitle class="sr-only">{{ title }}</DialogTitle>
            <DialogDescription class="sr-only">{{ description }}</DialogDescription>

            <DialogClose class="profile-video-close" aria-label="关闭企业简介视频">
              <i class="i-lucide-x" aria-hidden="true"></i>
            </DialogClose>

            <div class="profile-video-media">
              <video
                v-if="!videoFailed"
                ref="videoElement"
                :src="videoUrl"
                :poster="poster || undefined"
                controls
                autoplay
                playsinline
                preload="metadata"
                aria-label="海南兴科企业简介视频"
                @error="handleVideoError"
                @play="handleVideoPlay"
              >
                您的浏览器暂不支持视频播放。
              </video>

              <div v-else class="profile-video-error" role="alert">
                <i class="i-lucide-circle-alert" aria-hidden="true"></i>
                <strong>视频暂时无法播放</strong>
                <span>请稍后重试，或点击重新加载。</span>
                <button type="button" @click="retryVideo">重新加载</button>
              </div>

              <p v-if="autoplayBlocked && !videoFailed" class="profile-video-hint" role="status">
                请点击视频播放按钮开始播放
              </p>
            </div>
          </div>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import { nextTick, onBeforeUnmount, shallowRef, useTemplateRef, watch } from 'vue'

  import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
  } from 'reka-ui'

  interface Props {
    /** 视频说明。 */
    description?: string
    /** 弹窗是否打开。 */
    open: boolean
    /** 视频封面地址。 */
    poster?: string
    /** 弹窗标题。 */
    title?: string
    /** 视频文件地址。 */
    videoUrl: string
  }

  defineOptions({
    name: 'ProfileVideoDialog',
  })

  const props = withDefaults(defineProps<Props>(), {
    description: '以中试验证连接实验室研究与产业应用',
    poster: undefined,
    title: '海南兴科',
  })

  const emit = defineEmits<{
    'update:open': [open: boolean]
  }>()

  const videoElement = useTemplateRef<HTMLVideoElement>('videoElement')
  const videoFailed = shallowRef(false)
  const autoplayBlocked = shallowRef(false)

  /** 播放器真正开始播放后清理自动播放提示。 */
  function handleVideoPlay() {
    autoplayBlocked.value = false
  }

  /** 远程视频加载失败时切换为可理解的错误状态。 */
  function handleVideoError() {
    videoFailed.value = true
    autoplayBlocked.value = false
  }

  /** 尝试调用原生播放 API；浏览器拦截时保留控件供用户手动播放。 */
  async function playVideo() {
    await nextTick()
    const video = videoElement.value
    if (!video || videoFailed.value || !props.open) return

    try {
      await video.play()
    } catch {
      if (props.open) autoplayBlocked.value = true
    }
  }

  /** 停止并重置播放器，避免关闭弹窗后继续占用媒体资源。 */
  function resetVideo() {
    const video = videoElement.value
    if (video) {
      video.pause()
      if (video.readyState > 0) video.currentTime = 0
    }
    videoFailed.value = false
    autoplayBlocked.value = false
  }

  /** 重新挂载播放器并再次尝试播放。 */
  async function retryVideo() {
    videoFailed.value = false
    autoplayBlocked.value = false
    await playVideo()
  }

  /** 处理遮罩、关闭按钮和 Escape 触发的受控关闭。 */
  function close() {
    if (props.open) emit('update:open', false)
  }

  /** 同步 DialogRoot 的受控开合状态。 */
  function handleOpenChange(nextOpen: boolean) {
    emit('update:open', nextOpen)
  }

  watch(
    () => [props.open, props.videoUrl] as const,
    ([isOpen]) => {
      if (!isOpen) {
        resetVideo()
        return
      }

      videoFailed.value = false
      autoplayBlocked.value = false
      void playVideo()
    },
    { immediate: true }
  )

  onBeforeUnmount(resetVideo)
</script>

<style scoped lang="scss">
  .profile-video-overlay {
    position: fixed;
    inset: 0;
    z-index: 79;
    background: rgb(24 12 14 / 78%);
    backdrop-filter: blur(5px);
  }

  .profile-video-overlay-enter-active,
  .profile-video-overlay-leave-active {
    transition: opacity 220ms var(--ease);
  }

  .profile-video-overlay-enter-from,
  .profile-video-overlay-leave-to {
    opacity: 0;
  }

  .profile-video-content-enter-active,
  .profile-video-content-leave-active {
    transition:
      opacity 220ms var(--ease),
      transform 220ms var(--ease);
  }

  .profile-video-content-enter-from,
  .profile-video-content-leave-to {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }

  .profile-video-dialog {
    position: fixed;
    inset: 0;
    z-index: 80;
    width: min(980px, calc(100vw - 48px));
    max-width: none;
    height: fit-content;
    max-height: calc(100vh - 48px);
    padding: 0;
    margin: auto;
    overflow: hidden;
    color: var(--white);
    outline: none;
    background: #171719;
    border: 0;
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-modal);
  }

  .profile-video-shell {
    position: relative;
    max-height: calc(100vh - 48px);
    overflow-y: auto;
  }

  .profile-video-close {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 3;
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    padding: 0;
    color: var(--white);
    cursor: pointer;
    background: rgb(0 0 0 / 34%);
    border: 1px solid rgb(255 255 255 / 46%);
    border-radius: 50%;
  }

  .profile-video-close:hover {
    color: #ff9da3;
    border-color: #ff9da3;
  }

  .profile-video-close:focus-visible,
  .profile-video-media button:focus-visible {
    outline: 2px solid #ff9da3;
    outline-offset: 3px;
  }

  .profile-video-close i {
    width: 21px;
    height: 21px;
  }

  .profile-video-media {
    position: relative;
    display: grid;
    place-items: center;
    min-height: 420px;
    background: #09090a;
  }

  .profile-video-media video {
    display: block;
    width: 100%;
    max-height: min(68vh, 620px);
    aspect-ratio: 16 / 9;
    object-fit: contain;
    background: #09090a;
  }

  .profile-video-error {
    display: grid;
    gap: 10px;
    place-items: center;
    min-height: 420px;
    padding: 48px 24px;
    color: rgb(255 255 255 / 78%);
    text-align: center;
  }

  .profile-video-error i {
    width: 34px;
    height: 34px;
  }

  .profile-video-error strong {
    color: var(--white);
  }

  .profile-video-error button {
    min-width: 108px;
    padding: 9px 18px;
    color: var(--white);
    cursor: pointer;
    background: transparent;
    border: 1px solid rgb(255 255 255 / 62%);
    border-radius: 999px;
  }

  .profile-video-error button:hover {
    background: rgb(255 255 255 / 12%);
  }

  .profile-video-hint {
    position: absolute;
    right: 20px;
    bottom: 52px;
    left: 20px;
    margin: 0;
    font-size: 12px;
    color: rgb(255 255 255 / 76%);
    text-align: center;
    pointer-events: none;
  }

  @media (max-width: 767px) {
    .profile-video-dialog {
      width: calc(100vw - 24px);
      max-height: calc(100vh - 24px);
    }

    .profile-video-shell {
      max-height: calc(100vh - 24px);
    }

    .profile-video-media,
    .profile-video-error {
      min-height: 240px;
    }
  }
</style>
